// ============================================================================
// "You entered $0. That's fine — which is it?" 2026-09-03, Ben.
//
// ⛔ THE FIRST WORD IS "THAT'S FINE". Ben: *"$0 is acceptable as a number, BUT IT
// MUST BE THERE."* This is not a validation error and must never read like one —
// the AM has done nothing wrong. It asks which kind of zero it is, because the
// four flagged records in the book meant four different things by it.
//
// ⛔ IT IS NOT A BLOCK. Every path out of here saves. "Leave it as it is" is a
// real answer and it is always available, including with Escape and the ✕ —
// a dialog that cannot be dismissed is a dialog people learn to fear.
//
// The rules — when it fires, what the answers mean, and the undo — live in
// lib/zero-prompt.ts with their tests. This file only draws them.
// ============================================================================
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { ZERO_ANSWERS, type ZeroAnswer, type ZeroSide } from '../lib/zero-prompt';

const CHOICE = 'w-full rounded-lg border px-3 py-2.5 text-left text-sm transition';
const CHOICE_OFF =
  'border-tcs-border bg-tcs-raised text-tcs-text hover:border-tcs-text-muted';

const HEADING: Record<ZeroSide, string> = {
  client: 'You entered $0 for the client. That’s fine — which is it?',
  sub: 'You entered $0 for the sub. That’s fine — which is it?',
};

/**
 * ⭐ AFB-0085 (1) AND (2) — THE SAME ANSWERS, REACHED WITHOUT TYPING A ZERO.
 *
 * Ben's decisions asked for *"an amount box, OR three taps"*. The typed-$0 path
 * only helps an AM who already decided to write 0; the one who does not know
 * what to put still has an empty box and no way out, which is how AFB-0085
 * describes people inventing a number or abandoning the record.
 *
 * So the same list opens from a button beside the box. Identical answers,
 * identical consequences — only the question above them changes, because "you
 * entered $0" would be a lie about something they never typed.
 */
const HEADING_UNTYPED: Record<ZeroSide, string> = {
  client: 'Not sure what to charge the client? Which is it?',
  sub: 'Not sure what to pay the sub? Which is it?',
};

interface Props {
  open: boolean;
  side: ZeroSide;
  /**
   * Did the AM type a zero, or ask for help with an empty box? Only the wording
   * differs — every answer and every consequence is the same.
   */
  entered?: boolean;
  onClose: () => void;
  /** The AM picked a real answer. `opensHelper` answers are passed up as-is. */
  onAnswer: (answer: ZeroAnswer) => void;
}

export function ZeroPromptDialog({ open, side, entered = true, onClose, onAnswer }: Props) {
  const heading = entered ? HEADING[side] : HEADING_UNTYPED[side];
  // Escape closes it. A $0 prompt that traps you is worse than no prompt.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  const answers = ZERO_ANSWERS[side];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
    >
      <div className="w-full max-w-md rounded-xl border border-tcs-border bg-tcs-bg p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[15px] font-semibold leading-snug text-tcs-text">
            {heading}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-2">
          {answers.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => onAnswer(a)}
              className={`${CHOICE} ${CHOICE_OFF}`}
            >
              <span className="block font-semibold">{a.prompt}</span>
              <span className="mt-0.5 block text-[12px] text-tcs-text-secondary">
                {a.detail}
              </span>
            </button>
          ))}
        </div>

        {/* ⛔ ALWAYS PRESENT. The zero stands on its own; answering is optional. */}
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-lg px-3 py-2 text-[12.5px] text-tcs-text-secondary hover:bg-tcs-surface hover:text-tcs-text"
        >
          {entered ? 'Leave it as it is' : 'Never mind — I’ll type a number'}
        </button>
      </div>
    </div>
  );
}
